import {
  type FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import {
  add,
  addDays,
  addHours,
  isBefore,
  isValid,
  parse,
  set,
} from "date-fns";
import { TimeInput } from "semantic-ui-calendar-react";
import uuid from "uuid";

import { formatDate } from "simplydo/core/dates";
import { setLocalStorageItem } from "simplydo/core/helpers";
import { MSTeamsIcon } from "simplydo/react/components/Branding/MSTeams";
import { RichText } from "simplydo/react/richtext";
import {
  Accordion,
  Button,
  Checkbox,
  Divider,
  Dropdown,
  Form,
  Icon,
  Label,
  Message,
  Segment,
  Table,
} from "simplydo/react/ui";
import { Input } from "simplydo/react/ui";

import api from "api";
import { useAppSelector } from "store";
import { sdiClickDomain } from "utils/domains";
import util from "utils/utils";

import UserChip from "components/lib/Chips/UserChip";
import type { ChipUser } from "components/lib/Chips/types";
import { FileChooserWithDropzone, UserChooser } from "components/lib/Choosers";
import type { ChoosableUser as ChooserUser } from "components/lib/Choosers/types";
import { DateInput, DateTimeInput } from "components/lib/DateInputs";
import UserConnectionManager from "components/lib/UserConnectionManager";

import TimezoneExplainer from "../TimezoneExplainer";
import type { AnnouncementSetState, EditableAnnouncement } from "./types";

type InputChangeHandler = (_e: FormEvent, data: { value: string }) => void;

type CheckboxChangeHandler = (
  _e: FormEvent,
  data: { checked?: boolean },
) => void;

type TextAreaChangeHandler = (
  _e: FormEvent,
  data: { value?: string | number },
) => void;

const formatTeamsMeetingTemplate = (teamsMeeting: {
  name: string;
  startDate: string;
  endDate: string;
  link: string;
}) => {
  return `<p></p><p>Join <b>${teamsMeeting.name}</b> in Microsoft Teams (${formatDate(teamsMeeting.startDate)} - ${formatDate(teamsMeeting.endDate)}): <a href="${teamsMeeting.link}">${teamsMeeting.link}</a></p>`;
};

const defaultSendAt = set(addDays(new Date(), 1), {
  hours: 9,
  minutes: 0,
  seconds: 0,
  milliseconds: 0,
}).toISOString();

type AnnouncementBaseProps = {
  forType: string;
  forId: string;
  announcement: EditableAnnouncement;
  setAnnouncement: AnnouncementSetState;
};

const AnnouncementBase = ({
  forType,
  forId,
  announcement,
  setAnnouncement,
}: AnnouncementBaseProps) => {
  const { t } = useTranslation();
  const user = useAppSelector((state) => state.user);
  const [renamingAttachmentId, setRenamingAttachmentId] = useState("");
  const [renamingAttachmentName, setRenamingAttachmentName] = useState("");
  const [activeAccordionIndexes, setActiveAccordionIndexes] = useState<
    number[]
  >([]);
  const [creatingTeamsMeeting, setCreatingTeamsMeeting] = useState(false);
  const [teamsMeetingName, setTeamsMeetingName] = useState(
    announcement.teamsMeeting?.subject || "",
  );
  const [teamsMeetingDescription, setTeamsMeetingDescription] = useState(
    announcement.teamsMeeting?.description || "",
  );
  const [teamsMeetingStartDate, setTeamsMeetingStartDate] = useState(
    announcement.teamsMeeting?.startDate
      ? new Date(announcement.teamsMeeting.startDate).toISOString()
      : new Date().toISOString(),
  );
  const [teamsMeetingEndDate, setTeamsMeetingEndDate] = useState(
    announcement.teamsMeeting?.endDate
      ? new Date(announcement.teamsMeeting.endDate).toISOString()
      : add(new Date(), { hours: 1 }).toISOString(),
  );
  const [teamsMeetingLink, setTeamsMeetingLink] = useState(
    announcement.teamsMeeting?.joinWebUrl || "",
  );

  const userIsAdmin = util.hasPermission(
    user,
    "org.viewDashboard",
    user?.ownerOrganisation?._id,
  );

  const toggleActiveAccordion = useCallback((index: number) => {
    setActiveAccordionIndexes((prevState) => {
      if (prevState.includes(index)) {
        return prevState.filter((i) => i !== index);
      } else {
        return [...prevState, index];
      }
    });
  }, []);

  const { calendarEvent } = announcement;

  useEffect(() => {
    if (announcement?._id) return;
    setLocalStorageItem(`announcement-${forId}`, JSON.stringify(announcement));
  }, [announcement, forId]);

  const addAttachments = useCallback(
    (files: { savedName: string; fileName: string }[]) => {
      toast.success("Upload successful");
      setAnnouncement((prevState) => ({
        ...prevState,
        attachments: [
          ...(prevState.attachments ?? []),
          ...files.map((file) => ({
            value: file.savedName,
            name: file.fileName,
            _id: uuid.v4(),
          })),
        ],
      }));
    },
    [setAnnouncement],
  );

  const onChange = useCallback(
    (value: string, text: string) => {
      setAnnouncement((prevState) => ({ ...prevState, body: value, text }));
    },
    [setAnnouncement],
  );

  const createTeamsMeeting = useCallback(() => {
    if (!teamsMeetingName) {
      toast.error("Meeting name is required.");
      return;
    }
    if (
      !isValid(new Date(teamsMeetingStartDate)) ||
      !isValid(new Date(teamsMeetingEndDate))
    ) {
      toast.error("Please select valid start and end dates.");
      return;
    }
    if (isBefore(teamsMeetingEndDate, teamsMeetingStartDate)) {
      toast.error("Meeting end date must be after the start date.");
      return;
    }

    setCreatingTeamsMeeting(true);
    api.announcements.createTeamsMeeting(
      forType,
      forId,
      {
        name: teamsMeetingName,
        description: teamsMeetingDescription,
        startDate: teamsMeetingStartDate,
        endDate: teamsMeetingEndDate,
      },
      ({ meeting }) => {
        const joinWebUrl = meeting?.joinWebUrl;
        if (!joinWebUrl) {
          toast.error("Meeting created but no join URL was returned.");
          setCreatingTeamsMeeting(false);
          return;
        }

        setTeamsMeetingLink(joinWebUrl);
        setAnnouncement((prevState) => {
          return {
            ...prevState,
            body: `${prevState.body || ""}${formatTeamsMeetingTemplate({
              name: meeting.subject!,
              startDate: teamsMeetingStartDate,
              endDate: teamsMeetingEndDate,
              link: joinWebUrl,
            })}`,
            teamsMeeting: meeting,
          };
        });
        toast.success("Teams meeting created.");
        setCreatingTeamsMeeting(false);
      },
      (err) => {
        toast.error(err.message);
        setCreatingTeamsMeeting(false);
      },
    );
  }, [
    forType,
    forId,
    teamsMeetingName,
    teamsMeetingDescription,
    teamsMeetingStartDate,
    teamsMeetingEndDate,
    setAnnouncement,
  ]);

  const customRecipientSelector = useMemo(() => {
    if (userIsAdmin) {
      return (
        <>
          <Divider hidden />
          <h5 style={{ marginBottom: 5 }}>To user</h5>
          <p>
            When your announcement is triggered the announcement will be sent to
            the user that triggered the announcement. For example, if a an
            announcement is sent on submission of {t("generic.ideaWithArticle")}
            , the user that submitted the {t("generic.idea")} will be contacted.
            If you want to change this, please search for recipients below.
          </p>
          <Dropdown
            text={
              announcement?.toUserCondition === "ideaOwner"
                ? "Idea authors"
                : announcement?.toUserCondition === "customUsers"
                  ? "Select custom users"
                  : "User that triggered the condition"
            }
            className="selection"
          >
            <Dropdown.Menu>
              <Dropdown.Item
                onClick={() =>
                  setAnnouncement((prevState) => ({
                    ...prevState,
                    toUserCondition: "userThatTriggered",
                  }))
                }
              >
                User that triggered the condition.
              </Dropdown.Item>
              {forType === "challenge" && (
                <Dropdown.Item
                  onClick={() =>
                    setAnnouncement((prevState) => ({
                      ...prevState,
                      toUserCondition: "ideaOwner",
                    }))
                  }
                >
                  {t("common:capitalise", { key: "generic.idea" })} authors
                </Dropdown.Item>
              )}
              <Dropdown.Item
                onClick={() =>
                  setAnnouncement((prevState) => ({
                    ...prevState,
                    toUserCondition: "customUsers",
                  }))
                }
              >
                Select custom users:
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
          {announcement?.toUserCondition === "customUsers" && (
            <>
              <UserChooser
                selectedUsers={
                  (announcement.ownerToUsers || []) as ChooserUser[]
                }
                trigger={
                  <Button
                    style={{ marginTop: 5, marginLeft: 10 }}
                    icon="plus"
                    content="Add custom recipient"
                  />
                }
                enabledFeatures={{ search: true }}
                searchFunction={(query, success, failure) => {
                  if (!query) {
                    success([]);
                    return;
                  }
                  api.search.omni(
                    { query, include: ["users"] },
                    ({ users }) => users && success(users),
                    failure,
                  );
                }}
                onComplete={(users) => {
                  if (users.length === 0) {
                    return;
                  }
                  setAnnouncement((prevState) => ({
                    ...prevState,
                    ownerToUsers: users as EditableAnnouncement["ownerToUsers"],
                    toUsers: users.map((u) => u._id),
                  }));
                }}
              />
              {announcement?.ownerToUsers?.length !== 0 ? (
                <div>
                  {announcement?.ownerToUsers?.map((recipient) => (
                    <div
                      key={recipient._id}
                      style={{
                        display: "flex",
                        marginTop: 20,
                        justifyContent: "space-between",
                        alignItems: "center",
                        maxWidth: "fit-content",
                      }}
                    >
                      <UserChip target="_blank" user={recipient as ChipUser} />
                      <Button
                        basic
                        icon="trash"
                        size="mini"
                        onClick={() =>
                          setAnnouncement((prevState) => ({
                            ...prevState,
                            toUsers: (prevState.toUsers ?? []).filter(
                              (id) => id !== recipient._id,
                            ),
                            ownerToUsers: (prevState.ownerToUsers ?? []).filter(
                              (u) => u._id !== recipient._id,
                            ),
                          }))
                        }
                      />
                    </div>
                  ))}
                </div>
              ) : null}
            </>
          )}
        </>
      );
    }
    return null;
  }, [userIsAdmin, announcement, setAnnouncement, t, forType]);

  const fullScreenSelector = useMemo(() => {
    return (
      <>
        <Divider hidden />
        <h5 style={{ marginBottom: 5 }}>Full-screen announcement</h5>
        <p>
          If enabled, {util.appName()} will also show this announcement in
          &quot;full-screen mode&quot; for the recipient, increasing the chance
          that they read it at the point of delivery.
        </p>
        <Checkbox
          toggle
          label="Enable full screen announcement"
          checked={announcement.fullScreen}
          onChange={(_e, { checked }) =>
            setAnnouncement((prevState) => ({
              ...prevState,
              fullScreen: checked,
            }))
          }
        />
      </>
    );
  }, [announcement, setAnnouncement]);

  const updateSendAt = useCallback(
    (type: "date" | "time", value: string) => {
      const currentSendAt = announcement.sendAt ?? "";
      const date =
        type === "date"
          ? value
          : currentSendAt
            ? formatDate(currentSendAt, "yyyy-MM-dd")
            : value;
      const time =
        type === "time"
          ? value
          : currentSendAt
            ? formatDate(currentSendAt, "HH:mm")
            : value;
      const newDate = parse(`${date} ${time}`, "yyyy-MM-dd HH:mm", new Date());
      setAnnouncement((prev) => ({
        ...prev,
        sendAt: isValid(newDate) ? newDate.toISOString() : value,
      }));
    },
    [announcement.sendAt, setAnnouncement],
  );

  const sendAtValid = useMemo(() => {
    if (!announcement.sendAt) return false;
    return isValid(new Date(announcement.sendAt));
  }, [announcement.sendAt]);
  const scheduledDate = useMemo(
    () =>
      announcement.sendAt ? formatDate(announcement.sendAt, "yyyy-MM-dd") : "",
    [announcement.sendAt],
  );
  const scheduledTime = useMemo(
    () => (announcement.sendAt ? formatDate(announcement.sendAt, "HH:mm") : ""),
    [announcement.sendAt],
  );

  return (
    <Form>
      <Segment>
        <h3>Content</h3>
        Customise the content of the announcement to be sent to users.
        <Message info size="small">
          <h4>
            <Icon name="lightbulb outline" />
            {t("announcements.form.placeholders.title")}
          </h4>
          <p>{t("announcements.form.placeholders.info")}</p>
          <ul>
            <li>
              <code>
                <mark>{"{firstName}"}</mark>
              </code>{" "}
              {t("announcements.form.placeholders.firstName")}
            </li>
            <li>
              <code>
                <mark>{"{lastName}"}</mark>
              </code>{" "}
              {t("announcements.form.placeholders.lastName")}
            </li>
          </ul>
        </Message>
        <Divider hidden />
        <Form.Field
          control={Input}
          label={t("announcements.form.subject")}
          placeholder={`${t("announcements.form.subject")}...`}
          required
          value={announcement.subject}
          onChange={
            ((_e, { value }) =>
              setAnnouncement((prevState) => ({
                ...prevState,
                subject: value,
              }))) as InputChangeHandler
          }
        />
        <Divider hidden />
        <Form.Field
          required
          label={t("announcements.form.body")}
          style={{ margin: 0 }}
        />
        <RichText
          preset="simple"
          fullMentionLinks
          autoHideMenu={false}
          placeholder={t("announcements.form.message")}
          value={announcement.body}
          onChange={onChange}
        />
      </Segment>
      <Accordion styled fluid>
        <Accordion.Title
          index={0}
          active={activeAccordionIndexes.includes(0)}
          onClick={() => toggleActiveAccordion(0)}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
            }}
          >
            <h3 style={{ marginBottom: 0 }}>Settings</h3>
            {announcement?.replyTo && (
              <Label
                style={{ marginLeft: 10 }}
                color="blue"
                size="small"
                content={`Reply-to: ${announcement?.replyTo}`}
              />
            )}
            {announcement?.callToActionText && (
              <Label
                style={{ marginLeft: 10 }}
                color="blue"
                size="small"
                content={`Call-to action: ${announcement.callToActionText}`}
              />
            )}
            {announcement?.toUsers && (
              <Label
                style={{ marginLeft: 10 }}
                color="blue"
                size="small"
                content={
                  announcement?.toUsers?.length === 1
                    ? `To user: ${announcement?.ownerToUsers?.[0]?.profile?.fullName}`
                    : `To users: ${announcement?.ownerToUsers?.[0]?.profile?.fullName} and ${util.pluralise((announcement?.ownerToUsers?.length ?? 1) - 1, "other", "others", true)}`
                }
              />
            )}
          </div>
        </Accordion.Title>
        <Accordion.Content active={activeAccordionIndexes.includes(0)}>
          <h5 style={{ marginBottom: 5 }}>Reply-to address</h5>
          <p style={{ marginBottom: 5 }}>
            A reply-to address is the designated email where responses to your
            email announcement will be sent.
          </p>
          <Form.Field
            control={Input}
            placeholder={t("placeholder.email")}
            width={5}
            value={announcement.replyTo}
            onChange={
              ((_e, { value }) =>
                setAnnouncement((prevState) => ({
                  ...prevState,
                  replyTo: value,
                }))) as InputChangeHandler
            }
          />
          <Divider hidden />
          <h5 style={{ marginBottom: 5 }}>Call-to-action</h5>
          <p>
            A call to action is a customizable button displayed at the end of
            your email that directs users to your desired destination.
          </p>
          <Form.Group>
            <Form.Field
              control={Input}
              fluid
              label="Text"
              placeholder={`View on ${util.appName()}`}
              width={5}
              value={announcement.callToActionText}
              onChange={
                ((_e, { value }) =>
                  setAnnouncement((prevState) => ({
                    ...prevState,
                    callToActionText: value,
                  }))) as InputChangeHandler
              }
            />
            <Form.Field
              control={Input}
              fluid
              label="URL"
              placeholder={`${sdiClickDomain}/linkTo${t("common:capitalise", { key: "generic.challenge" })}`}
              width={7}
              value={announcement.callToActionLink}
              onChange={
                ((_e, { value }) =>
                  setAnnouncement((prevState) => ({
                    ...prevState,
                    callToActionLink: value,
                  }))) as InputChangeHandler
              }
            />
            <Form.Button
              label="Preview"
              style={{
                background: "#14435b",
                color: "white",
                fontWeight: 500,
                minWidth: 170,
              }}
            >
              {announcement.callToActionText || `View on ${util.appName()}`}
            </Form.Button>
          </Form.Group>
          {announcement?.type === "automatic" && customRecipientSelector}
          {announcement?.type === "automatic" && fullScreenSelector}
        </Accordion.Content>
        <Accordion.Title
          index={1}
          active={activeAccordionIndexes.includes(1)}
          onClick={() => toggleActiveAccordion(1)}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
            }}
          >
            <h3 style={{ marginBottom: 0 }}>Attachments</h3>
            {(announcement.attachments?.length ?? 0) > 0 && (
              <Label
                style={{ marginLeft: 10 }}
                color="blue"
                size="small"
                content={`${util.pluralise(announcement.attachments?.length ?? 0, "attachment", "attachments", true)}`}
              />
            )}
          </div>
        </Accordion.Title>
        <Accordion.Content active={activeAccordionIndexes.includes(1)}>
          <p>
            Attach additional files to the announcement. Recipients will be able
            to download and access the attached files from the announcement.
          </p>
          {(announcement.attachments?.length ?? 0) > 0 ? (
            <Table basic="very">
              {announcement.attachments?.map((file) => (
                <Table.Row key={file._id}>
                  <Table.Cell>
                    {renamingAttachmentId === file._id ? (
                      <Input
                        size="mini"
                        fluid
                        defaultValue={renamingAttachmentName}
                        onChange={(_e, { value }) => {
                          setRenamingAttachmentName(value);
                        }}
                        action={{
                          size: "tiny",
                          icon: "save",
                          content: "Done",
                          onClick: () => {
                            setAnnouncement((prevState) => ({
                              ...prevState,
                              attachments: (prevState.attachments ?? []).map(
                                (attachment) =>
                                  attachment._id === file._id
                                    ? {
                                        ...attachment,
                                        name: renamingAttachmentName,
                                      }
                                    : attachment,
                              ),
                            }));
                            setRenamingAttachmentId("");
                            setRenamingAttachmentName("");
                          },
                        }}
                      />
                    ) : (
                      file.name
                    )}
                  </Table.Cell>
                  <Table.Cell collapsing>
                    {!renamingAttachmentId ? (
                      <Button
                        content="Rename"
                        icon="edit"
                        size="tiny"
                        onClick={() => {
                          setRenamingAttachmentId(file._id);
                          setRenamingAttachmentName(file.name);
                        }}
                        basic
                      />
                    ) : null}
                    <Button
                      icon="trash"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        util
                          .confirm(
                            "Remove attachment",
                            "Are you sure you want to remove this attachment?",
                          )
                          .then(() => {
                            setAnnouncement((prevState) => ({
                              ...prevState,
                              attachments: (prevState.attachments ?? []).filter(
                                (attachment) => attachment._id !== file._id,
                              ),
                            }));
                          })
                          .catch(() => {});
                      }}
                      size="tiny"
                    />
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table>
          ) : null}
          <FileChooserWithDropzone
            maxMBFileSize={10}
            forType={forType}
            forId={forId}
            allowMultiple={true}
            onCompleteMultiple={addAttachments}
            onError={toast.error}
            popupProps={{ position: "bottom center", disabled: false }}
          />
        </Accordion.Content>
        <Accordion.Title
          index={2}
          onClick={() => toggleActiveAccordion(2)}
          active={activeAccordionIndexes.includes(2)}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
            }}
          >
            <h3 style={{ marginBottom: 0 }}>Meeting</h3>
            {teamsMeetingName && (
              <Label
                style={{ marginLeft: 10 }}
                color="blue"
                size="small"
                content={teamsMeetingName}
              />
            )}
          </div>
        </Accordion.Title>
        <Accordion.Content active={activeAccordionIndexes.includes(2)}>
          <p>
            Create a Microsoft Teams meeting link, it will be displayed as a
            link in your announcement.
          </p>
          <UserConnectionManager
            entity="user"
            entityId={user?._id ?? ""}
            serviceProvider="microsoft"
            service="microsoftTeamsMeetings"
            contextId="microsoftTeamsMeetings"
            onConnect={(refreshCallback) => {
              toast.success("Microsoft Teams meetings connected");
              refreshCallback?.();
            }}
            onConnectionError={(errorMessage) => {
              toast.error(
                errorMessage || "Unable to connect Microsoft Teams meetings.",
              );
            }}
            renderDisconnected={({ connect, loading, isConnecting }) => (
              <Button
                type="button"
                size="small"
                disabled={loading || isConnecting}
                loading={loading || isConnecting}
                onClick={connect}
              >
                Connect Microsoft Teams
              </Button>
            )}
            renderConnected={() => (
              <>
                {teamsMeetingLink ? (
                  <Segment>
                    <div
                      style={{ display: "flex", flexDirection: "row", gap: 10 }}
                    >
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 10,
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "row",
                            gap: 3,
                          }}
                        >
                          <Icon
                            name="calendar"
                            size="large"
                            style={{ marginTop: 3 }}
                          />
                          <div>
                            <h4 style={{ margin: 0 }}>
                              {teamsMeetingName || "Teams meeting"}
                            </h4>
                            <p>
                              {formatDate(teamsMeetingStartDate)} -{" "}
                              {formatDate(teamsMeetingEndDate)}
                            </p>
                          </div>
                        </div>
                      </div>
                      <div style={{ marginLeft: "auto" }}>
                        <Input
                          value={teamsMeetingLink}
                          readOnly
                          action={{
                            icon: "copy",
                            size: "small",
                            onClick: () => {
                              navigator.clipboard.writeText(teamsMeetingLink);
                              toast.success("Meeting link copied to clipboard");
                            },
                          }}
                          size="small"
                          style={{ marginRight: 45 }}
                        />
                        <Button
                          basic
                          size="small"
                          icon="trash"
                          onClick={() => {
                            util
                              .confirm(
                                "Remove Microsoft Teams meeting",
                                "Are you sure you want to delete this Microsoft Teams meeting?",
                              )
                              .then(() => {
                                setTeamsMeetingLink("");
                                setTeamsMeetingName("");
                                setTeamsMeetingStartDate(
                                  new Date().toISOString(),
                                );
                                setTeamsMeetingEndDate(
                                  addHours(new Date(), 1).toISOString(),
                                );
                                setTeamsMeetingDescription("");
                                setAnnouncement((prevState) => ({
                                  ...prevState,
                                  teamsMeeting: null,
                                  body: prevState.body.replace(
                                    formatTeamsMeetingTemplate({
                                      name: teamsMeetingName,
                                      startDate: teamsMeetingStartDate,
                                      endDate: teamsMeetingEndDate,
                                      link: teamsMeetingLink,
                                    }),
                                    "",
                                  ),
                                }));
                              })
                              .catch(() => {});
                          }}
                        />
                      </div>
                    </div>
                    {!calendarEvent ? (
                      <Message style={{ marginTop: 10 }}>
                        <div>
                          <p>
                            At the moment this announcement has no calendar
                            event attached. You can automatically create one
                            based on this meeting, this will enable recipients
                            to add the event as a reminder to their own
                            calendar.
                          </p>
                        </div>
                        <Button
                          size="small"
                          content="Create calendar event"
                          onClick={() => {
                            setAnnouncement((prevState) => ({
                              ...prevState,
                              calendarEvent: {
                                ...prevState.calendarEvent,
                                startDate: teamsMeetingStartDate,
                                endDate: teamsMeetingEndDate,
                                organiserName: user?.profile?.fullName,
                                organiserEmail: user?.emails[0]?.address,
                                name: teamsMeetingName,
                                location: "",
                                description: teamsMeetingDescription,
                              },
                            }));
                          }}
                        />
                      </Message>
                    ) : null}
                  </Segment>
                ) : (
                  <Form widths="equal" style={{ marginTop: 10 }}>
                    <Form.Group>
                      <Form.Field
                        control={Input}
                        label="Meeting name"
                        placeholder="Meeting name"
                        value={teamsMeetingName}
                        onChange={
                          ((_e, { value }) =>
                            setTeamsMeetingName(value)) as InputChangeHandler
                        }
                      />
                      <DateTimeInput
                        label="Start date"
                        placeholder="Start date"
                        name="teamsMeetingStartDate"
                        dateTimeFormat="yyyy-MM-dd HH:mm"
                        value={new Date(teamsMeetingStartDate)}
                        iconPosition="left"
                        onChange={
                          ((_e, { value }) => {
                            const nextStartDate = parse(
                              value,
                              "yyyy-MM-dd HH:mm",
                              new Date(),
                            ).toISOString();
                            setTeamsMeetingStartDate(nextStartDate);
                            if (isBefore(teamsMeetingEndDate, nextStartDate)) {
                              setTeamsMeetingEndDate(
                                addHours(nextStartDate, 1).toISOString(),
                              );
                            }
                          }) as InputChangeHandler
                        }
                      />
                      <DateTimeInput
                        label="End date"
                        placeholder="End date"
                        name="teamsMeetingEndDate"
                        minDate={new Date(teamsMeetingStartDate)}
                        value={new Date(teamsMeetingEndDate)}
                        dateTimeFormat="yyyy-MM-dd HH:mm"
                        iconPosition="left"
                        onChange={
                          ((_e, { value }) =>
                            setTeamsMeetingEndDate(
                              parse(
                                value,
                                "yyyy-MM-dd HH:mm",
                                new Date(),
                              ).toISOString(),
                            )) as InputChangeHandler
                        }
                      />
                    </Form.Group>
                    <Form.TextArea
                      label="Meeting description"
                      placeholder="(optional)"
                      value={teamsMeetingDescription}
                      onChange={
                        ((_e, { value }) =>
                          setTeamsMeetingDescription(
                            String(value || ""),
                          )) as TextAreaChangeHandler
                      }
                    />
                    <Button
                      type="button"
                      size="small"
                      loading={creatingTeamsMeeting}
                      disabled={!teamsMeetingName}
                      onClick={createTeamsMeeting}
                    >
                      <MSTeamsIcon
                        size={16}
                        style={{
                          marginBottom: -3,
                          marginTop: -3,
                          marginRight: 5,
                        }}
                      />
                      Create Teams meeting
                    </Button>
                  </Form>
                )}
              </>
            )}
          />
        </Accordion.Content>
        <Accordion.Title
          index={3}
          active={activeAccordionIndexes.includes(3)}
          onClick={() => toggleActiveAccordion(3)}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
            }}
          >
            <h3 style={{ marginBottom: 0 }}>Calendar event</h3>
            {calendarEvent?.name && (
              <Label
                style={{ marginLeft: 10 }}
                color="blue"
                size="small"
                content={calendarEvent?.name}
              />
            )}
          </div>
        </Accordion.Title>
        <Accordion.Content active={activeAccordionIndexes.includes(3)}>
          <p>
            Create a calendar event that will be attached to emails sent with
            this announcement. This will enable recipients to add the event
            reminder to their own calendar.
          </p>

          <Button
            size="small"
            onClick={(e) => {
              if (calendarEvent) {
                setAnnouncement((prevState) => ({
                  ...prevState,
                  calendarEvent: null,
                }));
              } else {
                setAnnouncement((prevState) => ({
                  ...prevState,
                  calendarEvent: {
                    ...prevState.calendarEvent,
                    startDate: new Date().toISOString(),
                    endDate: addHours(new Date(), 1).toISOString(),
                    organiserName: user?.profile?.fullName,
                    organiserEmail: user?.emails[0]?.address,
                  },
                }));
              }
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            {calendarEvent ? "Remove calendar event" : "Add calendar event"}
          </Button>
          {calendarEvent && (
            <>
              <Divider hidden />
              <Form widths="equal" style={{ marginTop: 10, marginBottom: 10 }}>
                <Form.Group>
                  {calendarEvent.startDate ? (
                    <DateTimeInput
                      label="Start date"
                      placeholder="Start date"
                      name="startDate"
                      dateTimeFormat="yyyy-MM-dd HH:mm"
                      value={new Date(calendarEvent.startDate)}
                      iconPosition="left"
                      onChange={
                        ((_e, { value }) => {
                          if (announcement?.calendarEvent?.endDate) {
                            setAnnouncement((prevState) => ({
                              ...prevState,
                              calendarEvent: {
                                ...prevState.calendarEvent,
                                startDate: parse(
                                  value,
                                  "yyyy-MM-dd HH:mm",
                                  new Date(),
                                ).toISOString(),
                              },
                            }));
                          } else {
                            setAnnouncement((prevState) => ({
                              ...prevState,
                              calendarEvent: {
                                ...prevState.calendarEvent,
                                startDate: parse(
                                  value,
                                  "yyyy-MM-dd HH:mm",
                                  new Date(),
                                ).toISOString(),
                                endDate: addHours(
                                  parse(value, "yyyy-MM-dd HH:mm", new Date()),
                                  1,
                                ).toISOString(),
                              },
                            }));
                          }
                        }) as InputChangeHandler
                      }
                    />
                  ) : null}
                  {calendarEvent.endDate ? (
                    <DateTimeInput
                      label="End date"
                      placeholder="End date"
                      name="endDate"
                      minDate={
                        new Date(
                          calendarEvent.startDate ?? calendarEvent.endDate,
                        )
                      }
                      dateTimeFormat="yyyy-MM-dd HH:mm"
                      value={new Date(calendarEvent.endDate)}
                      iconPosition="left"
                      onChange={
                        ((_e, { value }) =>
                          setAnnouncement((prevState) => ({
                            ...prevState,
                            calendarEvent: {
                              ...prevState.calendarEvent,
                              endDate: parse(
                                value,
                                "yyyy-MM-dd HH:mm",
                                new Date(),
                              ).toISOString(),
                            },
                          }))) as InputChangeHandler
                      }
                    />
                  ) : null}
                </Form.Group>
                <Form.Group>
                  <Form.Field
                    control={Input}
                    error={
                      !calendarEvent.organiserName
                        ? "Organiser name is required"
                        : false
                    }
                    label="Organiser name"
                    placeholder="Organiser name"
                    value={calendarEvent.organiserName}
                    onChange={
                      ((_e, { value }) =>
                        setAnnouncement((prevState) => ({
                          ...prevState,
                          calendarEvent: {
                            ...prevState.calendarEvent,
                            organiserName: value,
                          },
                        }))) as InputChangeHandler
                    }
                  />
                  <Form.Field
                    control={Input}
                    error={
                      !calendarEvent.organiserEmail
                        ? "Organiser email is required"
                        : false
                    }
                    label="Organiser email"
                    type="email"
                    placeholder="Organiser email"
                    onChange={
                      ((_e, { value }) =>
                        setAnnouncement((prevState) => ({
                          ...prevState,
                          calendarEvent: {
                            ...prevState.calendarEvent,
                            organiserEmail: value,
                          },
                        }))) as InputChangeHandler
                    }
                    value={calendarEvent.organiserEmail}
                  />
                </Form.Group>
                <Message>
                  The resulting calendar invitation will be displayed as
                  originating from this name and email address.
                </Message>
                <Form.Group>
                  <Form.Field
                    control={Input}
                    label="Event name"
                    placeholder="Event name"
                    value={calendarEvent.name}
                    onChange={
                      ((_e, { value }) =>
                        setAnnouncement((prevState) => ({
                          ...prevState,
                          calendarEvent: {
                            ...prevState.calendarEvent,
                            name: value,
                          },
                        }))) as InputChangeHandler
                    }
                  />
                  <Form.Field
                    control={Input}
                    label="Location"
                    placeholder="(optional)"
                    onChange={
                      ((_e, { value }) =>
                        setAnnouncement((prevState) => ({
                          ...prevState,
                          calendarEvent: {
                            ...prevState.calendarEvent,
                            location: value,
                          },
                        }))) as InputChangeHandler
                    }
                    value={calendarEvent.location}
                  />
                </Form.Group>
                <Form.TextArea
                  label="Description"
                  placeholder="(optional)"
                  value={calendarEvent.description}
                  onChange={
                    ((_e, { value }) =>
                      setAnnouncement((prevState) => ({
                        ...prevState,
                        calendarEvent: {
                          ...prevState.calendarEvent,
                          description: String(value ?? ""),
                        },
                      }))) as TextAreaChangeHandler
                  }
                />
              </Form>
              <p>{`The calendar invite will be displayed as originating from your email address "${user?.emails[0]?.address}". Note that RSVPs may be received at this address.`}</p>
            </>
          )}
        </Accordion.Content>

        {(announcement.type === "standard" ||
          announcement.type === "scheduled") && (
          <>
            <Accordion.Title
              index={4}
              onClick={() => toggleActiveAccordion(4)}
              active={activeAccordionIndexes.includes(4)}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                }}
              >
                <h3 style={{ marginBottom: 0 }}>Send when</h3>
                {announcement.sendAt && (
                  <Label
                    style={{ marginLeft: 10 }}
                    color="blue"
                    size="small"
                    content={formatDate(
                      announcement.sendAt,
                      "MMMM do yyyy 'at' HH:mm",
                    )}
                  />
                )}
              </div>
            </Accordion.Title>
            <Accordion.Content active={activeAccordionIndexes.includes(4)}>
              <p>
                This announcement can be scheduled to be sent at a later date
                instead of right away.
              </p>
              <Checkbox
                slider
                label="Schedule this announcement to be sent at a later date"
                checked={!!announcement.sendAt}
                onChange={
                  ((_e, { checked }) =>
                    setAnnouncement((prev) => ({
                      ...prev,
                      sendAt: checked ? prev.sendAt || defaultSendAt : null,
                    }))) as CheckboxChangeHandler
                }
              />
              {announcement.sendAt ? (
                <>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      marginTop: 10,
                    }}
                  >
                    <span>Send announcement on</span>
                    <DateInput
                      name="date"
                      placeholder="Date"
                      value={sendAtValid ? scheduledDate : announcement.sendAt}
                      minDate={new Date()}
                      dateFormat="yyyy-MM-dd"
                      iconPosition="left"
                      onChange={(_e, { value }) =>
                        updateSendAt("date", value ?? "")
                      }
                      style={{
                        marginLeft: 10,
                        marginRight: 10,
                        marginBottom: -10,
                      }}
                    />
                    <span style={{ marginLeft: 20 }}> at </span>
                    <TimeInput
                      name="time"
                      placeholder="Time"
                      value={sendAtValid ? scheduledTime : announcement.sendAt}
                      disableMinute
                      iconPosition="left"
                      onChange={(_e, { value }) =>
                        updateSendAt("time", value ?? "")
                      }
                      style={{
                        marginLeft: 10,
                        marginRight: 10,
                        marginBottom: 5,
                      }}
                    />
                  </div>
                  <TimezoneExplainer style={{ marginTop: 10 }} />
                </>
              ) : null}
            </Accordion.Content>
          </>
        )}
      </Accordion>
    </Form>
  );
};

export default AnnouncementBase;
